import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { MapPin, Mail, Send, CheckCircle, Loader2, Phone, User } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    product_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      toast.success("Enquiry submitted successfully!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Header */}
      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
            Get In Touch
          </p>
          <h1
            data-testid="contact-heading"
            className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4"
          >
            Contact Us
          </h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">
            Enquire about product supply, request specifications, or discuss
            partnership opportunities.
          </p>
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-white border border-[#E2E0D5] rounded-xl p-8">
              {submitted ? (
                <div
                  data-testid="enquiry-success"
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <CheckCircle size={48} className="text-novapine-gold mb-4" />
                  <h3 className="font-heading font-bold text-2xl text-novapine-brown mb-2">
                    Enquiry Received
                  </h3>
                  <p className="font-body text-base text-novapine-brown/60 max-w-sm">
                    Thank you for your interest. Our team will review your
                    enquiry and respond within 1-2 business days.
                  </p>
                  <Button
                    data-testid="submit-another-btn"
                    className="btn-primary-novapine rounded-md px-6 mt-8 font-heading text-sm"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        email: "",
                        company: "",
                        phone: "",
                        product_interest: "",
                        message: "",
                      });
                    }}
                  >
                    Submit Another Enquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="enquiry-form">
                  <h3 className="font-heading font-bold text-xl text-novapine-brown mb-6">
                    Enquire About Supply
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label
                        htmlFor="name"
                        className="font-heading text-sm text-novapine-brown mb-1.5 block"
                      >
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        placeholder="Your name"
                        value={form.name}
                        onChange={handleChange}
                        className="border-[#E2E0D5] focus:ring-2 focus:ring-novapine-brown/20 font-body"
                        required
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="email"
                        className="font-heading text-sm text-novapine-brown mb-1.5 block"
                      >
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={handleChange}
                        className="border-[#E2E0D5] focus:ring-2 focus:ring-novapine-brown/20 font-body"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label
                        htmlFor="company"
                        className="font-heading text-sm text-novapine-brown mb-1.5 block"
                      >
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="input-company"
                        placeholder="Company name"
                        value={form.company}
                        onChange={handleChange}
                        className="border-[#E2E0D5] focus:ring-2 focus:ring-novapine-brown/20 font-body"
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="phone"
                        className="font-heading text-sm text-novapine-brown mb-1.5 block"
                      >
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="input-phone"
                        placeholder="+27..."
                        value={form.phone}
                        onChange={handleChange}
                        className="border-[#E2E0D5] focus:ring-2 focus:ring-novapine-brown/20 font-body"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <Label
                      htmlFor="product_interest"
                      className="font-heading text-sm text-novapine-brown mb-1.5 block"
                    >
                      Product Interest
                    </Label>
                    <Select
                      value={form.product_interest}
                      onValueChange={(val) =>
                        setForm((prev) => ({ ...prev, product_interest: val }))
                      }
                    >
                      <SelectTrigger
                        data-testid="select-product-interest"
                        className="border-[#E2E0D5] font-body"
                      >
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="frozen-pulp">
                          Frozen Pineapple Pulp
                        </SelectItem>
                        <SelectItem value="frozen-chunks">
                          Frozen Pineapple Chunks
                        </SelectItem>
                        <SelectItem value="dried-pineapple">
                          Dried Pineapple
                        </SelectItem>
                        <SelectItem value="whole-fruit">
                          Whole Fruit Supply
                        </SelectItem>
                        <SelectItem value="multiple">
                          Multiple Products
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mb-6">
                    <Label
                      htmlFor="message"
                      className="font-heading text-sm text-novapine-brown mb-1.5 block"
                    >
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      placeholder="Tell us about your requirements, volumes, or any questions..."
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      className="border-[#E2E0D5] focus:ring-2 focus:ring-novapine-brown/20 font-body resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-enquiry-btn"
                    className="btn-gold rounded-md w-full py-5 font-heading text-sm tracking-wide font-semibold"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} className="mr-2" />
                        Submit Enquiry
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Map + Info */}
            <div className="flex flex-col gap-6">
              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white border border-[#E2E0D5] rounded-xl p-5">
                  <User size={20} className="text-novapine-gold mb-3" />
                  <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">
                    Lloyd Domleo
                  </h4>
                  <a href="tel:+27722528030" className="font-body text-sm text-novapine-brown/60 hover:text-novapine-brown transition-colors">
                    +27 72 252 8030
                  </a>
                </div>
                <div className="bg-white border border-[#E2E0D5] rounded-xl p-5">
                  <User size={20} className="text-novapine-gold mb-3" />
                  <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">
                    James Domleo
                  </h4>
                  <a href="tel:+27825598740" className="font-body text-sm text-novapine-brown/60 hover:text-novapine-brown transition-colors">
                    +27 82 559 8740
                  </a>
                </div>
                <div className="bg-white border border-[#E2E0D5] rounded-xl p-5">
                  <MapPin size={20} className="text-novapine-gold mb-3" />
                  <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">
                    Location
                  </h4>
                  <p className="font-body text-sm text-novapine-brown/60">
                    Hluhluwe, KwaZulu-Natal
                    <br />
                    South Africa
                  </p>
                </div>
                <div className="bg-white border border-[#E2E0D5] rounded-xl p-5">
                  <Mail size={20} className="text-novapine-gold mb-3" />
                  <h4 className="font-heading font-semibold text-sm text-novapine-brown mb-1">
                    Email
                  </h4>
                  <p className="font-body text-sm text-novapine-brown/60">
                    info@novapine.co.za
                  </p>
                </div>
              </div>

              {/* Google Map */}
              <div
                data-testid="google-map"
                className="bg-white border border-[#E2E0D5] rounded-xl overflow-hidden flex-1 min-h-[400px]"
              >
                <iframe
                  title="Novapine Location - Hluhluwe, KwaZulu-Natal"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28541.88477757!2d32.2400!3d-28.0200!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef1b8f0b1b1b1b1%3A0x1b1b1b1b1b1b1b1b!2sHluhluwe%2C%20KwaZulu-Natal%2C%20South%20Africa!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "400px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
